import React, { useEffect, useMemo, useState } from "react";
import { StepUpSpinner } from "../../../Components/Loader";
import {
  Grid,
  Box,
  Button,
  TextField,
  InputAdornment,
  Card,
  Typography,
  Stack,
  Chip,
  IconButton,
  Tooltip,
  MenuItem,
  Avatar,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  useCreateUserPrivilegeMutation,
  useDeleteUserPrivilegeMutation,
  useGetUserInfoOnyQuery,
  useGetUserPrivilegeQuery,
} from "../../../Api/attoDeskApi";
import { IPrivilege, IUserAdmin } from "../../../Api/Interface/api.interface";
import DeletePopup from "../../../Components/Delete/DeletePopup";
import { useNotifier } from "../../../Core/Notifier";
import { CiSearch } from "react-icons/ci";
import { useStyles } from "../../../theme/appStyles";
import useIsMobile from "../../../Core/hook/useIsMobile";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineSharpIcon from "@mui/icons-material/DeleteOutlineSharp";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import {
  CRUD,
  CRUD_ACTIONS,
  Crud,
  deriveCategory,
  humanizeModule,
  parsePrivilegeName,
} from "../../UserRole/Components/permissionUtils";
import PrivilegeModuleModal, {
  EditingModule,
} from "../Components/PrivilegeModuleModal";

interface ModuleEntry {
  token: string;
  category: string;
  actions: Record<Crud, IPrivilege | null>;
  rows: IPrivilege[];
}

const CATEGORY_ORDER = [
  "Dashboard",
  "Student Management",
  "Academic",
  "Staff / HR",
  "Finance",
  "Communication",
  "Reports",
  "Admin",
  "Other",
];

const categoryIcon = (cat: string) => {
  const sx = { fontSize: 20, color: "#475569" };
  const c = cat.toLowerCase();
  if (c.includes("dashboard")) return <DashboardOutlinedIcon sx={sx} />;
  if (c.includes("student")) return <SchoolOutlinedIcon sx={sx} />;
  if (c.includes("academic")) return <MenuBookOutlinedIcon sx={sx} />;
  if (c.includes("staff") || c.includes("hr")) return <BadgeOutlinedIcon sx={sx} />;
  if (c.includes("finance")) return <PaymentsOutlinedIcon sx={sx} />;
  if (c.includes("commun")) return <ChatBubbleOutlineIcon sx={sx} />;
  if (c.includes("report")) return <AssessmentOutlinedIcon sx={sx} />;
  if (c.includes("admin")) return <SettingsOutlinedIcon sx={sx} />;
  return <FolderOutlinedIcon sx={sx} />;
};

const ActionCell = ({
  on,
  onClick,
  disabled,
}: {
  on: boolean;
  onClick?: () => void;
  disabled?: boolean;
}) => (
  <IconButton
    size="small"
    onClick={onClick}
    disabled={disabled || !onClick}
    sx={{ p: 0.25 }}
  >
    {on ? (
      <CheckCircleIcon sx={{ color: "#16a34a", fontSize: 22 }} />
    ) : (
      <CheckBoxOutlineBlankIcon sx={{ color: "#cbd5e1", fontSize: 20 }} />
    )}
  </IconButton>
);

const Index = () => {
  const classes = useStyles();
  const isMobile = useIsMobile();
  const { showErrorMessage, showMessage } = useNotifier();
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  const [modal, setModal] = useState<{
    open: boolean;
    mode: "create" | "edit";
    module: EditingModule | null;
  }>({ open: false, mode: "create", module: null });

  const [moduleToDelete, setModuleToDelete] = useState<ModuleEntry | null>(null);

  const { data, isLoading, isError, refetch } = useGetUserPrivilegeQuery({
    searchText: "",
  });
  const { data: userData } = useGetUserInfoOnyQuery({ searchText: "" });

  const userMap = useMemo(() => {
    const map = new Map<string, string>();
    ((userData?.data as IUserAdmin[]) || []).forEach((u) =>
      map.set(String(u.id), u.username)
    );
    return map;
  }, [userData?.data]);

  const privileges: IPrivilege[] = useMemo(
    () => (Array.isArray(data?.data) ? (data?.data as IPrivilege[]) : []),
    [data?.data]
  );

  const existingNames = useMemo(
    () => new Set(privileges.map((p) => p.name.toLowerCase())),
    [privileges]
  );

  // Build modules (CRUD) + flags (non-CRUD) dynamically from the names.
  const { modules, flags } = useMemo(() => {
    const map = new Map<string, ModuleEntry>();
    const flagList: IPrivilege[] = [];
    privileges.forEach((p) => {
      const { module, action } = parsePrivilegeName(p.name);
      if (!action) {
        flagList.push(p);
        return;
      }
      if (!map.has(module)) {
        map.set(module, {
          token: module,
          category: deriveCategory(module),
          actions: { View: null, Create: null, Edit: null, Delete: null },
          rows: [],
        });
      }
      const entry = map.get(module)!;
      entry.actions[action] = p;
      entry.rows.push(p);
    });
    return { modules: Array.from(map.values()), flags: flagList };
  }, [privileges]);

  // Filter by search + category.
  const term = searchQuery.trim().toLowerCase();
  const filteredModules = useMemo(
    () =>
      modules.filter((m) => {
        if (categoryFilter !== "all" && m.category !== categoryFilter)
          return false;
        if (!term) return true;
        return (
          humanizeModule(m.token).toLowerCase().includes(term) ||
          m.token.toLowerCase().includes(term)
        );
      }),
    [modules, term, categoryFilter]
  );
  const filteredFlags = useMemo(
    () =>
      flags.filter((f) => {
        const cat = deriveCategory(f.name);
        if (categoryFilter !== "all" && cat !== categoryFilter) return false;
        if (!term) return true;
        return (
          (f.display_name || "").toLowerCase().includes(term) ||
          f.name.toLowerCase().includes(term)
        );
      }),
    [flags, term, categoryFilter]
  );

  // Group into category sections.
  const sections = useMemo(() => {
    const byCat = new Map<
      string,
      { modules: ModuleEntry[]; flags: IPrivilege[] }
    >();
    filteredModules.forEach((m) => {
      if (!byCat.has(m.category)) byCat.set(m.category, { modules: [], flags: [] });
      byCat.get(m.category)!.modules.push(m);
    });
    filteredFlags.forEach((f) => {
      const cat = deriveCategory(f.name);
      if (!byCat.has(cat)) byCat.set(cat, { modules: [], flags: [] });
      byCat.get(cat)!.flags.push(f);
    });
    return Array.from(byCat.entries())
      .map(([category, v]) => ({
        category,
        modules: v.modules.sort((a, b) =>
          humanizeModule(a.token).localeCompare(humanizeModule(b.token))
        ),
        flags: v.flags,
      }))
      .sort(
        (a, b) =>
          CATEGORY_ORDER.indexOf(a.category) - CATEGORY_ORDER.indexOf(b.category)
      );
  }, [filteredModules, filteredFlags]);

  const allCategories = useMemo(
    () =>
      Array.from(
        new Set([
          ...modules.map((m) => m.category),
          ...flags.map((f) => deriveCategory(f.name)),
        ])
      ).sort(
        (a, b) => CATEGORY_ORDER.indexOf(a) - CATEGORY_ORDER.indexOf(b)
      ),
    [modules, flags]
  );

  const expandAll = () => setCollapsed({});
  const collapseAll = () =>
    setCollapsed(Object.fromEntries(allCategories.map((c) => [c, true])));

  const openCreate = () =>
    setModal({ open: true, mode: "create", module: null });
  const closeModal = () => setModal((p) => ({ ...p, open: false }));

  const [createPrivilege] = useCreateUserPrivilegeMutation();
  const [deletePrivilege] = useDeleteUserPrivilegeMutation();
  const [saving, setSaving] = useState(false);

  const newAction = (token: string, action: Crud) => ({
    name: `${token}_${action}`,
    display_name: `${humanizeModule(token)} ${action}`,
  });

  // Auto-save: run a batch of creates + deletes, then refetch and toast.
  const applyOps = async (
    creates: { name: string; display_name: string }[],
    deletes: number[]
  ) => {
    if (saving || (creates.length === 0 && deletes.length === 0)) return;
    setSaving(true);
    try {
      for (const c of creates) {
        const res: any = await createPrivilege(c).unwrap();
        if (res?.status === false) {
          showErrorMessage(res.message || "Update failed");
          return;
        }
      }
      for (const id of deletes) {
        const res: any = await deletePrivilege(String(id)).unwrap();
        if (res?.status === false) {
          showErrorMessage(res.message || "Update failed");
          return;
        }
      }
      showMessage("Permission updated");
    } catch (e: any) {
      showErrorMessage(e?.data?.message || "Update failed");
    } finally {
      setSaving(false);
      refetch();
    }
  };

  const toggleCell = (m: ModuleEntry, action: Crud) => {
    const row = m.actions[action];
    if (row) applyOps([], [row.id]);
    else applyOps([newAction(m.token, action)], []);
  };

  // Toggle all 4 actions for one module (its row).
  const toggleModuleAll = (m: ModuleEntry) => {
    const allOn = CRUD.every((a) => m.actions[a]);
    if (allOn)
      applyOps(
        [],
        CRUD.map((a) => m.actions[a]!.id)
      );
    else
      applyOps(
        CRUD.filter((a) => !m.actions[a]).map((a) => newAction(m.token, a)),
        []
      );
  };

  // Toggle a whole column (View/Create/Edit/Delete) across a category's modules.
  const toggleCategoryColumn = (mods: ModuleEntry[], action: Crud) => {
    const allOn = mods.length > 0 && mods.every((m) => m.actions[action]);
    if (allOn)
      applyOps(
        [],
        mods.map((m) => m.actions[action]!.id)
      );
    else
      applyOps(
        mods
          .filter((m) => !m.actions[action])
          .map((m) => newAction(m.token, action)),
        []
      );
  };

  // Toggle every permission in a category.
  const toggleCategoryAll = (mods: ModuleEntry[]) => {
    const allOn =
      mods.length > 0 && mods.every((m) => CRUD.every((a) => m.actions[a]));
    if (allOn)
      applyOps(
        [],
        mods.flatMap((m) =>
          CRUD.map((a) => m.actions[a]?.id).filter((x): x is number => !!x)
        )
      );
    else
      applyOps(
        mods.flatMap((m) =>
          CRUD.filter((a) => !m.actions[a]).map((a) => newAction(m.token, a))
        ),
        []
      );
  };

  const handleDeleteModule = async (m: ModuleEntry) => {
    try {
      const ids = CRUD.map((a) => m.actions[a]?.id).filter(
        (x): x is number => !!x
      );
      for (const id of ids) {
        const res: any = await deletePrivilege(String(id)).unwrap();
        if (res?.status === false) {
          showErrorMessage(res.message || "Failed to delete permission");
          return;
        }
      }
      showMessage(`"${humanizeModule(m.token)}" module deleted.`);
      setModuleToDelete(null);
      refetch();
    } catch (e) {
      showErrorMessage("Failed to delete the module");
    }
  };

  const subtitle = (m: ModuleEntry) => {
    const by = userMap.get(String(m.rows[0]?.created_by));
    const updatedAt = m.rows
      .map((r) => r.updated_at)
      .filter(Boolean)
      .sort()
      .pop();
    const parts: string[] = [];
    if (by) parts.push(`By ${by}`);
    if (updatedAt) parts.push(`Updated ${new Date(updatedAt).toLocaleDateString()}`);
    return parts.join(" • ");
  };

  // ---- module rows (desktop table) — click cells to toggle, headers toggle columns ----
  const renderTable = (mods: ModuleEntry[]) => {
    const groupAllOn =
      mods.length > 0 && mods.every((m) => CRUD.every((a) => m.actions[a]));
    const groupSome = mods.some((m) => CRUD.some((a) => m.actions[a]));
    return (
      <Table size="small">
        <TableHead>
          <TableRow sx={{ "& th": { border: 0, color: "#94a3b8", fontSize: 12 } }}>
            <TableCell padding="checkbox">
              <Tooltip title="Select all permissions in this group">
                <Checkbox
                  size="small"
                  checked={groupAllOn}
                  indeterminate={!groupAllOn && groupSome}
                  onChange={() => toggleCategoryAll(mods)}
                  disabled={saving}
                />
              </Tooltip>
            </TableCell>
            <TableCell>PERMISSION</TableCell>
            {CRUD_ACTIONS.map((a) => {
              const colOn = mods.length > 0 && mods.every((m) => m.actions[a.key]);
              return (
                <TableCell key={a.key} align="center">
                  <Tooltip title={`Toggle all ${a.key}`}>
                    <Box
                      onClick={() => !saving && toggleCategoryColumn(mods, a.key)}
                      sx={{
                        cursor: saving ? "default" : "pointer",
                        color: a.color,
                        fontWeight: 700,
                        userSelect: "none",
                        opacity: colOn ? 1 : 0.75,
                      }}
                    >
                      {a.key.toUpperCase()}
                    </Box>
                  </Tooltip>
                </TableCell>
              );
            })}
            <TableCell align="right">ACTIONS</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {mods.map((m) => {
            const rowAllOn = CRUD.every((a) => m.actions[a]);
            const rowSome = CRUD.some((a) => m.actions[a]);
            return (
              <TableRow key={m.token} hover sx={{ "& td": { borderColor: "#f1f5f9" } }}>
                <TableCell padding="checkbox">
                  <Tooltip title="Select all permissions in this module">
                    <Checkbox
                      size="small"
                      checked={rowAllOn}
                      indeterminate={!rowAllOn && rowSome}
                      onChange={() => toggleModuleAll(m)}
                      disabled={saving}
                    />
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Typography sx={{ fontWeight: 600, color: "#334155" }}>
                    {humanizeModule(m.token)}
                  </Typography>
                  {subtitle(m) && (
                    <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
                      {subtitle(m)}
                    </Typography>
                  )}
                </TableCell>
                {CRUD.map((a) => (
                  <TableCell key={a} align="center">
                    <ActionCell
                      on={!!m.actions[a]}
                      onClick={() => toggleCell(m, a)}
                      disabled={saving}
                    />
                  </TableCell>
                ))}
                <TableCell align="right">
                  <Tooltip title="Delete module">
                    <IconButton
                      size="small"
                      onClick={() => setModuleToDelete(m)}
                      sx={{ color: "#dc2626" }}
                      disabled={saving}
                    >
                      <DeleteOutlineSharpIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    );
  };

  // ---- module card (mobile) — tap actions to toggle ----
  const renderMobileCards = (mods: ModuleEntry[]) => (
    <Stack spacing={1}>
      {mods.map((m) => {
        const rowAllOn = CRUD.every((a) => m.actions[a]);
        const rowSome = CRUD.some((a) => m.actions[a]);
        return (
          <Box
            key={m.token}
            sx={{ p: 1.5, border: "1px solid #f1f5f9", borderRadius: 2 }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Checkbox
                  size="small"
                  checked={rowAllOn}
                  indeterminate={!rowAllOn && rowSome}
                  onChange={() => toggleModuleAll(m)}
                  disabled={saving}
                  sx={{ p: 0.25 }}
                />
                <Typography sx={{ fontWeight: 600, color: "#334155" }}>
                  {humanizeModule(m.token)}
                </Typography>
              </Stack>
              <IconButton
                size="small"
                onClick={() => setModuleToDelete(m)}
                sx={{ color: "#dc2626" }}
                disabled={saving}
              >
                <DeleteOutlineSharpIcon fontSize="small" />
              </IconButton>
            </Stack>
            <Stack direction="row" flexWrap="wrap" columnGap={2} rowGap={0.5} sx={{ mt: 0.5 }}>
              {CRUD_ACTIONS.map((a) => (
                <Stack key={a.key} direction="row" alignItems="center" spacing={0.5}>
                  <Typography sx={{ fontSize: 12, color: a.color, minWidth: 44 }}>
                    {a.key}
                  </Typography>
                  <ActionCell
                    on={!!m.actions[a.key]}
                    onClick={() => toggleCell(m, a.key)}
                    disabled={saving}
                  />
                </Stack>
              ))}
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );

  return (
    <Box sx={{ background: "#f8fafc", minHeight: "100%" }}>
      <Card className={classes.card} sx={{ background: "transparent", boxShadow: "none" }}>
        {/* Header */}
        <Grid container justifyContent="space-between" alignItems="center" spacing={2} sx={{ p: 2 }}>
          <Grid item>
            <Typography sx={{ fontWeight: 700, fontSize: 20, color: "#0f172a" }}>
              Privileges
            </Typography>
            <Typography sx={{ color: "#64748b", fontSize: 14 }}>
              Manage system modules and available actions
            </Typography>
          </Grid>
          <Grid item>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <TextField
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search permissions…"
                sx={{ background: "#fff", minWidth: 220 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CiSearch />
                    </InputAdornment>
                  ),
                }}
              />
              <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
                Add Module
              </Button>
            </Stack>
          </Grid>
        </Grid>

        {/* Stat + filter bar */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ sm: "center" }}
          sx={{ px: 2, pb: 1 }}
        >
          <Chip
            label={`${privileges.length} permissions`}
            sx={{ background: "#eef2ff", color: "#3730a3", fontWeight: 600 }}
          />
          <Chip
            label={`${modules.length} modules`}
            sx={{ background: "#ecfdf5", color: "#047857", fontWeight: 600 }}
          />
          <TextField
            select
            size="small"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            sx={{ background: "#fff", minWidth: 200 }}
          >
            <MenuItem value="all">All categories</MenuItem>
            {allCategories.map((c) => (
              <MenuItem key={c} value={c}>
                {c}
              </MenuItem>
            ))}
          </TextField>
          <Box sx={{ flex: 1 }} />
          {!isMobile && (
            <>
              <Button size="small" startIcon={<UnfoldMoreIcon />} onClick={expandAll}>
                Expand
              </Button>
              <Button size="small" startIcon={<UnfoldLessIcon />} onClick={collapseAll}>
                Collapse
              </Button>
            </>
          )}
        </Stack>

        {/* Body */}
        <Box sx={{ p: 2 }}>
          {isLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="30vh">
              <StepUpSpinner size="medium" />
            </Box>
          ) : isError ? (
            <Box sx={{ textAlign: "center", color: "#94a3b8", py: 6 }}>
              Error fetching data
            </Box>
          ) : sections.length === 0 ? (
            <Box sx={{ textAlign: "center", color: "#94a3b8", py: 6 }}>
              {term ? `No modules match "${searchQuery}".` : "No permissions available"}
            </Box>
          ) : (
            sections.map((sec) => {
              const isClosed = !!collapsed[sec.category];
              return isMobile ? (
                <Accordion
                  key={sec.category}
                  defaultExpanded
                  disableGutters
                  sx={{
                    mb: 1.5,
                    borderRadius: 2,
                    border: "1px solid #e2e8f0",
                    "&:before": { display: "none" },
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {categoryIcon(sec.category)}
                      <Typography sx={{ fontWeight: 700, color: "#0f172a" }}>
                        {sec.category}
                      </Typography>
                      <Chip size="small" label={sec.modules.length} sx={{ height: 20 }} />
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails>
                    {renderMobileCards(sec.modules)}
                    {sec.flags.length > 0 && (
                      <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 1 }}>
                        {sec.flags.map((f) => (
                          <Chip
                            key={f.id}
                            icon={<CheckCircleIcon sx={{ color: "#16a34a !important" }} />}
                            label={f.display_name || humanizeModule(f.name)}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                      </Stack>
                    )}
                  </AccordionDetails>
                </Accordion>
              ) : (
                <Card
                  key={sec.category}
                  sx={{
                    mb: 2,
                    borderRadius: 3,
                    border: "1px solid #e2e8f0",
                    boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
                    overflow: "hidden",
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ px: 2, py: 1.25, background: "#f8fafc", cursor: "pointer" }}
                    onClick={() =>
                      setCollapsed((p) => ({ ...p, [sec.category]: !p[sec.category] }))
                    }
                  >
                    <Avatar sx={{ bgcolor: "#fff", width: 34, height: 34, border: "1px solid #e2e8f0" }}>
                      {categoryIcon(sec.category)}
                    </Avatar>
                    <Typography sx={{ fontWeight: 700, color: "#0f172a", flex: 1 }}>
                      {sec.category}
                    </Typography>
                    <Chip size="small" label={`${sec.modules.length} modules`} />
                    <IconButton size="small">
                      {isClosed ? <UnfoldMoreIcon /> : <UnfoldLessIcon />}
                    </IconButton>
                  </Stack>
                  {!isClosed && (
                    <Box sx={{ px: 2, py: 1 }}>
                      {sec.modules.length > 0 && renderTable(sec.modules)}
                      {sec.flags.length > 0 && (
                        <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 1.5 }}>
                          {sec.flags.map((f) => (
                            <Chip
                              key={f.id}
                              icon={<CheckCircleIcon sx={{ color: "#16a34a !important" }} />}
                              label={f.display_name || humanizeModule(f.name)}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        </Stack>
                      )}
                    </Box>
                  )}
                </Card>
              );
            })
          )}
        </Box>
      </Card>

      {modal.open && (
        <PrivilegeModuleModal
          open={modal.open}
          mode={modal.mode}
          module={modal.module}
          existingNames={existingNames}
          onClose={closeModal}
          onSaved={refetch}
        />
      )}

      {moduleToDelete && (
        <DeletePopup
          open={!!moduleToDelete}
          handleCloseDelete={() => setModuleToDelete(null)}
          onConfirm={async () => {
            await handleDeleteModule(moduleToDelete);
          }}
          title="Delete Module"
          content={`Delete all permissions for "${humanizeModule(
            moduleToDelete.token
          )}"? This removes its View/Create/Edit/Delete entries.`}
        />
      )}
    </Box>
  );
};

export default Index;
